import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { renderAsync } from "docx-preview";
import PdfViewer from "../../components/pdf/PdfViewer";
import Spreadsheet from "react-spreadsheet";
import "./DocumentViewer.css";

interface DocumentViewerProps {
  file: string | null | File;
  name: string;
}

type FileType = "word" | "pdf" | "xlsx" | "ppt" | "image" | "text" | "video" | "unsupported";

const DocumentViewer: React.FC<DocumentViewerProps> = ({ file, name }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fileType, setFileType] = useState<FileType>("unsupported");
  const [fileUrl, setFileUrl] = useState<string>("");
  const [xlsxData, setXlsxData] = useState<Array<Array<{ value: any }>>>([]);
  const [textContent, setTextContent] = useState<string>("");
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>("");

  const detectFileType = (fileName: string): FileType => {
    const cleanName = fileName.split("?")[0];
    const ext = cleanName.split(".").pop()?.toLowerCase();

    if (!ext) return "unsupported";
    if (ext === "docx" || ext === "doc") return "word";
    if (ext === "pdf") return "pdf";
    if (ext === "xlsx") return "xlsx";
    if (ext === "ppt" || ext === "pptx") return "ppt";
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) return "image";
    if (ext === "txt") return "text";
    if (["mp4", "avi", "mov", "wmv", "flv", "webm", "mkv", "m4v"].includes(ext)) return "video";
    return "unsupported";
  };

  const loadSheetData = (wb: XLSX.WorkBook, sheetName: string) => {
    try {
      const worksheet = wb.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Filter out completely empty rows and ensure all rows have the same length
      const filteredData = (jsonData as any[][])
        .filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== ''))
        .map(row => {
          // Ensure all rows have the same length by padding with empty strings
          const maxLength = Math.max(...(jsonData as any[][]).map(r => r.length));
          while (row.length < maxLength) {
            row.push('');
          }
          return row;
        });

      // Excel-like grid: Show actual data with padding for grid effect
      const sheetRows = filteredData.length;
      const sheetCols = Math.max(...filteredData.map(row => row.length));

      const ROWS = Math.max(20, sheetRows); // Minimum 20 rows for grid effect
      const COLS = Math.max(10, sheetCols); // Minimum 10 columns for grid effect

      // No manual headers, just pad the actual table data into a grid
      const spreadsheetData = Array.from({ length: ROWS }, (_, i) =>
        Array.from({ length: COLS }, (_, j) =>
          filteredData[i] && filteredData[i][j] !== undefined ? { value: filteredData[i][j] } : { value: '' }
        )
      );

      setXlsxData(spreadsheetData);
    } catch (err) {
      console.error("Error loading sheet data:", err);
      setXlsxData([]);
    }
  };

  const handleSheetChange = (sheetName: string) => {
    if (workbook && sheetNames.includes(sheetName)) {
      setSelectedSheet(sheetName);
      loadSheetData(workbook, sheetName);
    }
  };

  useEffect(() => {
    if (!file) {
      setFileType("unsupported");
      setFileUrl("");
      setXlsxData([]);
      setTextContent("");
      setWorkbook(null);
      setSheetNames([]);
      setSelectedSheet("");
      return;
    }

    const isString = typeof file === "string";
    const fileName = isString ? file : file.name;
    const extType = detectFileType(fileName);
    setFileType(extType);

    if (extType === "xlsx") {
      const loadXlsx = async () => {
        try {
          const blob = isString ? await (await fetch(file)).blob() : file;
          const arrayBuffer = await blob.arrayBuffer();
          const wb = XLSX.read(arrayBuffer, { type: 'array' });

          // Store workbook and sheet names
          setWorkbook(wb);
          setSheetNames(wb.SheetNames);
          setSelectedSheet(wb.SheetNames[0]); // Default to first sheet

          // Load data for the first sheet
          loadSheetData(wb, wb.SheetNames[0]);
        } catch (err) {
          console.error("Error loading XLSX:", err);
          setXlsxData([]);
          setWorkbook(null);
          setSheetNames([]);
          setSelectedSheet("");
        }
      };
      loadXlsx();
    } else if (extType === "word") {
      const loadWord = async () => {
        try {
          const response = isString ? await fetch(file) : file;
          const arrayBuffer = await response.arrayBuffer();
          if (containerRef.current) {
            containerRef.current.innerHTML = "";
            await renderAsync(arrayBuffer, containerRef.current);
          }
        } catch (err) {
          console.error("Error loading Doc:", err);
        }
      };
      loadWord();
    } else if (extType === "ppt") {
      setFileUrl(isString ? file : URL.createObjectURL(file));
    } else if (extType === "text") {
      const loadText = async () => {
        try {
          const response = isString ? await fetch(file) : file;
          const text = isString ? await response.text() : await file.text();
          setTextContent(text);
        } catch (err) {
          console.error("Error loading text file:", err);
          setTextContent("Failed to load text content.");
        }
      };
      loadText();
    } else if (extType === "pdf") {
      setFileUrl(isString ? file : URL.createObjectURL(file));
    } else if (extType === "video") {
      setFileUrl(isString ? file : URL.createObjectURL(file));
    } else if (!isString) {
      const url = URL.createObjectURL(file);
      setFileUrl(url);
    } else {
      setFileUrl(file);
    }
  }, [file]);


  if (!file) return <p className="text-gray-500">No file selected</p>;

  switch (fileType) {
    case "word":
      return (
        <div
          ref={containerRef}
          className="docx-container"
          style={{ padding: 20, background: "#f5f5f5" }}
        />
      );
    case "pdf":
      return (
        <div className="pdf-container">
          <PdfViewer fileUrl={fileUrl} />
        </div>
      );
    case "image":
      return (
        <img
          src={fileUrl}
          alt="Uploaded preview"
          className="max-h-[600px] mx-auto"
        />
      );
    case "xlsx":
      return (
        <div style={{
          height: '80vh',
          width: '100%',
          border: '1px solid #ddd',
          borderRadius: '4px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Main spreadsheet area */}
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <Spreadsheet
              data={xlsxData}
              onChange={() => {}} // Read-only mode
              className="custom-spreadsheet"
            />
          </div>

          {/* Sheet tabs */}
          {sheetNames.length > 1 && (
            <div className="sheet-tabs-container">
              {sheetNames.map((sheetName) => (
                <button
                  key={sheetName}
                  className={`sheet-tab ${sheetName === selectedSheet ? 'active' : ''}`}
                  onClick={() => handleSheetChange(sheetName)}
                >
                  {sheetName}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    case "text":
      return (
        <div className="text-container" style={{ padding: 20, background: "#f5f5f5" }}>
          <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
            {textContent}
          </pre>
        </div>
      );
    case "ppt":
      return (
        <div className="ppt-container" style={{ height: "640px", width: "100%" }}>
          <iframe
            src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`}
            width="100%"
            height="100%"
            frameBorder="0"
            title="PowerPoint Viewer"
          >
            This browser does not support iframes.
          </iframe>
        </div>
      );
    case "video":
      return (
        <div className="video-container" style={{ width: "100%", maxWidth: "100%" }}>
          <video
            controls
            style={{
              width: "100%",
              maxHeight: "80vh",
              outline: "none",
            }}
            preload="metadata"
          >
            <source src={fileUrl} type={`video/${name.split(".").pop()?.toLowerCase() || "mp4"}`} />
            Your browser does not support the video tag.
          </video>
          <p className="text-sm text-gray-600 mt-2 text-center">{name}</p>
        </div>
      );
    default:
      return <p className="text-red-500">Unsupported file type.</p>;
  }
};

export default DocumentViewer;